import React, { useState } from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #07090e;
  --panel: #0d111a;
  --surface: #131824;
  --text: #f3f4f8;
  --dim: #9aa0b2;
  --faint: #636878;
  --accent: #00e599;
  --danger: #f43f5e;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.68;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 44px 22px 90px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(145deg, #00c47d, #00e599);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 26px;
  box-shadow: 0 8px 24px rgba(0, 229, 153, 0.2);
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 8px 0 6px;
  color: #ffffff;
`;

const Section = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 36px 0 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 24px;
  margin: 18px 0 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const Button = styled.button`
  background: linear-gradient(145deg, #00c47d, #00e599);
  color: #090d16;
  border: none;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMsg = styled.div`
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  border-radius: 12px;
  padding: 16px;
  color: #a7f3d0;
  font-size: 15px;
  margin-top: 14px;
`;

const ExpentryDelete = () => {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>💎</Badge>
          <div>
            <Brand>
              Expentry / <span>Track Your Expense</span>
            </Brand>
            <span style={{ fontSize: "13px", color: "var(--faint)" }}>
              Data &amp; Account Deletion Management
            </span>
          </div>
        </Header>

        <Title>Data Deletion &amp; Management</Title>
        <p style={{ color: "var(--dim)", fontSize: "16px", marginBottom: "24px" }}>
          Expentry is a <strong>100% offline, privacy-first personal finance application</strong>.
          We do not operate remote databases or store your financial income, expense records, or notes on cloud servers.
        </p>

        <Section>How to Delete Your Data Locally</Section>
        <Card>
          <p style={{ margin: "0 0 12px", color: "var(--text)", fontWeight: "600" }}>
            You maintain total sovereignty over your financial entries and can permanently delete them at any time:
          </p>
          <ol style={{ paddingLeft: "20px", color: "var(--dim)", fontSize: "15px", lineHeight: "1.7" }}>
            <li style={{ marginBottom: "8px" }}>
              <strong>Purge All Transactions:</strong> Open the app &rarr; navigate to the <strong>Profile</strong> tab &rarr;{" "}
              tap <strong style={{ color: "var(--danger)" }}>Clear All Transactions</strong>. This immediately and permanently
              wipes all recorded incomes, expenses, metrics, and balance history from device storage.
            </li>
            <li style={{ marginBottom: "8px" }}>
              <strong>Delete Individual Entries:</strong> Open the <strong>History</strong> tab &rarr; tap the red trash
              icon next to any transaction to immediately remove that single entry.
            </li>
            <li>
              <strong>Complete App Deletion:</strong> Uninstalling Expentry from your device or navigating to{" "}
              <strong>Android Settings &rarr; Apps &rarr; Expentry &rarr; Storage &rarr; Clear Storage</strong> permanently
              deletes all local application databases and preferences.
            </li>
          </ol>
        </Card>

        <Section>Submit a Formal Request</Section>
        <Card>
          <p style={{ margin: "0 0 14px", color: "var(--dim)", fontSize: "15px" }}>
            If you have questions regarding your data or require formal verification for compliance, please submit an inquiry below or contact us directly at{" "}
            <a href="mailto:hello@nimions.com" style={{ color: "var(--accent)" }}>
              hello@nimions.com
            </a>
            .
          </p>

          {submitted ? (
            <SuccessMsg>
              ✓ Thank you. Your inquiry has been received. Because Expentry does not store user accounts or financial entries on external cloud databases, there is no remote account or database record to remove. All data is exclusively managed on your local physical device.
            </SuccessMsg>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Input
                type="email"
                placeholder="Your email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Textarea
                placeholder="Optional inquiry or details"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <Button type="submit">Submit Request</Button>
            </Form>
          )}
        </Card>
      </Wrap>
    </Page>
  );
};

export default ExpentryDelete;
